import React, { useEffect, useState } from 'react';
import axios from 'axios';
import SellerLayout from './SellerLayout';

const SellerPickupEdit = () => {
  const [loading, setLoading] = useState(true);
  const [shopName, setShopName] = useState('');
  const [orderId, setOrderId] = useState(0);
  const [order, setOrder] = useState(null);
  const [orderItems, setOrderItems] = useState([]);
  const [orderStatus, setOrderStatus] = useState('');
  const [currentStatus, setCurrentStatus] = useState('NEW');
  const [statusMap, setStatusMap] = useState({});
  const [msg, setMsg] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const oid = Number(new URLSearchParams(window.location.search).get('order_id') || 0);

        const res = await axios.get(
          `/api/seller/pickup/edit-data?order_id=${encodeURIComponent(oid)}`,
          { withCredentials: true }
        );

        if (res.data && res.data.ok) {
          setShopName(res.data.shop_name || '');
          setOrderId(res.data.order_id || 0);
          setOrder(res.data.order || null);
          setOrderItems(Array.isArray(res.data.order_items) ? res.data.order_items : []);
          setOrderStatus(res.data.order_status || '');
          setCurrentStatus(res.data.current_status || 'NEW');
          setStatusMap(res.data.status_map || {});
        } else {
          alert(res.data?.error || '픽업 주문 정보를 불러오지 못했습니다.');
        }
      } catch (err) {
        const redirect = err?.response?.data?.redirect;
        const error = err?.response?.data?.error;
        if (redirect) {
          window.location.href = redirect;
          return;
        }
        alert(error || '픽업 주문 정보를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatNumber = (n) => Number(n || 0).toLocaleString('ko-KR');

  const setPickupStatus = async (newStatus) => {
    setMsg('');

    const form = new FormData();
    form.append('order_id', orderId);
    form.append('current_status', newStatus);

    try {
      const res = await fetch('/api/seller/kiosk/order/update', {
        method: 'POST',
        body: form,
        credentials: 'same-origin',
      });
      const data = await res.json();

      if (data.ok) {
        setMsg('success');
        setTimeout(() => {
          window.location.reload();
        }, 300);
      } else {
        setMsg(data.msg || '변경 실패');
      }
    } catch (e) {
      setMsg('요청 실패');
    }
  };

  if (loading) {
    return (
      <SellerLayout shopName={shopName}>
        <div style={styles.loading}>로딩중...</div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout shopName={shopName}>
      <div style={styles.topSpace}></div>
      <h3 style={styles.title}>매장픽업 주문 관리 (주문번호 #{orderId})</h3>
      <div style={styles.muted}>상점: {shopName}</div>

      <div style={styles.statusBox}>
        <strong>주문 상태: </strong>
        {orderStatus === '주문취소' ? (
          <>
            <span style={styles.badgeDanger}>주문취소</span>
            {order?.paymentkey ? (
              <form
                method="post"
                action="/bill/manage_receipt"
                target="_blank"
                style={styles.inlineForm}
              >
                <input type="hidden" name="paykey" value={order.paymentkey} />
                <button type="submit" style={styles.inlineLinkBtn}>[상세정보]</button>
              </form>
            ) : null}
          </>
        ) : (
          <span style={styles.badgeSuccess}>결제완료</span>
        )}
      </div>

      <h5 style={styles.sectionTitle}>주문 상품</h5>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th110}>상품ID</th>
            <th style={styles.th}>상품명</th>
            <th style={styles.th90}>수량</th>
            <th style={styles.th120}>단가</th>
          </tr>
        </thead>
        <tbody>
          {orderItems.length > 0 ? (
            orderItems.map((it, idx) => (
              <tr key={idx}>
                <td style={styles.td}>{Number(it.product_id || 0)}</td>
                <td style={styles.tdLeft}>{it.product_name || '-'}</td>
                <td style={styles.td}>{Number(it.quantity || 0)}</td>
                <td style={styles.td}>{formatNumber(it.price)}원</td>
              </tr>
            ))
          ) : (
            <tr>
              <td style={styles.td} colSpan={4}>주문 상품이 없습니다.</td>
            </tr>
          )}
        </tbody>
      </table>

      <h5 style={styles.sectionTitle}>픽업 진행상태</h5>

      <div style={styles.currentWrap}>
        현재: <span style={styles.currentLabel}>{statusMap[currentStatus] || currentStatus}</span>
        <span style={styles.currentCode}>({currentStatus})</span>
      </div>

      {orderStatus === '주문취소' ? (
        <div style={styles.warningAlert}>취소된 주문은 진행상태를 변경할 수 없습니다.</div>
      ) : (
        <>
          <div style={styles.statusButtons}>
            {Object.entries(statusMap).map(([code, label]) => (
              <button
                key={code}
                type="button"
                style={code === currentStatus ? styles.darkBtn : styles.outlineDarkBtn}
                onClick={() => setPickupStatus(code)}
              >
                {label}
              </button>
            ))}
          </div>

          <div style={styles.msgWrap}>
            {msg === 'success' ? (
              <div style={styles.successAlert}>상태가 변경되었습니다.</div>
            ) : msg ? (
              <div style={styles.dangerAlert}>{msg}</div>
            ) : null}
          </div>
        </>
      )}

      <hr style={styles.hr} />

      <a href="/seller/orders" style={styles.secondaryBtn}>목록</a>
      <a href={`/seller/order/view?id=${orderId}`} style={styles.outlineSecondaryBtn}>주문상세</a>
    </SellerLayout>
  );
};

const styles = {
  loading: { padding: '40px 0' },
  topSpace: { height: '8px' },
  title: { marginBottom: '8px' },
  muted: { color: '#6c757d', marginBottom: '16px' },
  statusBox: { marginBottom: '16px' },
  badgeSuccess: {
    display: 'inline-block',
    minWidth: '80px',
    textAlign: 'center',
    background: '#198754',
    color: '#fff',
    borderRadius: '999px',
    padding: '4px 8px',
    marginLeft: '8px',
    fontSize: '12px',
    fontWeight: 600,
  },
  badgeDanger: {
    display: 'inline-block',
    minWidth: '80px',
    textAlign: 'center',
    background: '#dc3545',
    color: '#fff',
    borderRadius: '999px',
    padding: '4px 8px',
    marginLeft: '8px',
    fontSize: '12px',
    fontWeight: 600,
  },
  inlineForm: {
    display: 'inline',
    margin: 0,
    padding: 0,
  },
  inlineLinkBtn: {
    border: 'none',
    background: 'transparent',
    padding: 0,
    marginLeft: '8px',
    fontSize: '12px',
    color: '#000',
    cursor: 'pointer',
  },
  sectionTitle: { marginTop: '20px', marginBottom: '12px' },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    border: '1px solid #dee2e6',
    padding: '12px',
    background: '#f8f9fa',
    textAlign: 'left',
  },
  th110: {
    width: '110px',
    border: '1px solid #dee2e6',
    padding: '12px',
    background: '#f8f9fa',
    textAlign: 'left',
  },
  th90: {
    width: '90px',
    border: '1px solid #dee2e6',
    padding: '12px',
    background: '#f8f9fa',
    textAlign: 'left',
  },
  th120: {
    width: '120px',
    border: '1px solid #dee2e6',
    padding: '12px',
    background: '#f8f9fa',
    textAlign: 'left',
  },
  td: {
    border: '1px solid #dee2e6',
    padding: '12px',
    verticalAlign: 'middle',
  },
  tdLeft: {
    border: '1px solid #dee2e6',
    padding: '12px',
    textAlign: 'left',
    verticalAlign: 'middle',
  },
  currentWrap: { marginBottom: '8px' },
  currentLabel: { fontWeight: 700, marginLeft: '4px' },
  currentCode: { color: '#6c757d', marginLeft: '8px' },
  warningAlert: {
    padding: '12px 14px',
    borderRadius: '8px',
    background: '#fff3cd',
    color: '#664d03',
    border: '1px solid #ffecb5',
  },
  statusButtons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  darkBtn: {
    minWidth: '110px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #212529',
    background: '#212529',
    color: '#fff',
    cursor: 'pointer',
  },
  outlineDarkBtn: {
    minWidth: '110px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #212529',
    background: '#fff',
    color: '#212529',
    cursor: 'pointer',
  },
  msgWrap: { marginTop: '12px' },
  successAlert: {
    padding: '10px 12px',
    borderRadius: '8px',
    background: '#d1e7dd',
    color: '#0f5132',
    border: '1px solid #badbcc',
  },
  dangerAlert: {
    padding: '10px 12px',
    borderRadius: '8px',
    background: '#f8d7da',
    color: '#842029',
    border: '1px solid #f5c2c7',
  },
  hr: {
    margin: '24px 0',
    border: 0,
    borderTop: '1px solid #dee2e6',
  },
  secondaryBtn: {
    display: 'inline-block',
    textDecoration: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    background: '#6c757d',
    color: '#fff',
    marginRight: '8px',
  },
  outlineSecondaryBtn: {
    display: 'inline-block',
    textDecoration: 'none',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #6c757d',
    color: '#6c757d',
    background: '#fff',
  },
};

export default SellerPickupEdit;